import { useRouter } from 'vue-router'
import { parseConfigCode } from '@/services/configCode'
import { useEventsStore } from '@/stores/events'
import { useAuthStore } from '@/stores/auth'
import type { ApplyConfigResult } from '@/stores/events'

export interface ApplyOutcome {
    status: 'applied' | 'notConfig' | 'needsLogin' | 'failed'
    result?: ApplyConfigResult
}

// Shared core for applying a config payload, whether it arrived from the camera
// (ConfigScannerView) or from a deep-link ?cmd= at startup (App.vue). Parses the
// raw payload, enforces the auth edge case, applies via the events store and
// navigates (scanner when a selection was applied, otherwise home).
export function useApplyConfig() {
    const router = useRouter()
    const events = useEventsStore()
    const auth = useAuthStore()

    async function apply(raw: string): Promise<ApplyOutcome> {
        const parsed = parseConfigCode(raw)
        if (!parsed.isConfig) {
            return { status: 'notConfig' }
        }

        const hasLogin = parsed.commands.some(c => c.type === 'LOGIN')
        const hasSelection = parsed.commands.some(c => c.type === 'TICKETS_T' || c.type === 'TICKETS_Z')
        if (!auth.user && !hasLogin && hasSelection) {
            return { status: 'needsLogin' }
        }

        const result = await events.applyConfigCode(parsed.commands)
        if (!result.ok) {
            return { status: 'failed', result }
        }

        const appliedSelection = result.applied.some(a => a.type === 'TICKETS_T' || a.type === 'TICKETS_Z')
        await router.push({ name: appliedSelection ? 'scanner' : 'home' })
        return { status: 'applied', result }
    }

    return { apply }
}
